import { useState } from 'react';
import { MessageSquare, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CallContactDialog } from './CallContactDialog';
import { EmailContactDialog } from './EmailContactDialog';

interface Props {
  phone?: string | null;
  agentName?: string | null;
  agencyName?: string | null;
  agentLogo?: string | null;
  referenceId?: string | null;
  propertyId?: string;
  projectId?: string;
  listingType?: 'property' | 'project' | 'plot' | 'commercial';
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Three-button compact contact action row used on condensed listing cards:
 * Message (outline primary) · WhatsApp (brand green) · Call (solid primary)
 */
export function CompactContactActions({
  phone,
  agentName,
  agencyName,
  agentLogo,
  referenceId,
  propertyId,
  projectId,
  listingType = 'property',
  size = 'sm',
  className,
}: Props) {
  const [callOpen, setCallOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const wa = phone?.replace(/[^\d]/g, '');
  const h = size === 'md' ? 'h-9' : 'h-8';
  const icon = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5';

  const stop = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <>
      <div className={cn('flex items-center gap-1.5 w-full', className)} onClick={stop}>
        <button
          type="button"
          onClick={(e) => { stop(e); setMsgOpen(true); }}
          className={cn('flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-[11px] font-semibold', h)}
          aria-label="Message"
        >
          <MessageSquare className={icon} />
          <span className="hidden sm:inline">Message</span>
        </button>
        {wa ? (
          <a
            href={`https://wa.me/${wa}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={cn('flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-whatsapp text-whatsapp-foreground hover:opacity-90 transition-opacity text-[11px] font-semibold', h)}
            aria-label="WhatsApp"
          >
            <svg viewBox="0 0 24 24" className={icon} fill="currentColor" aria-hidden="true">
              <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.42z" />
            </svg>
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
        ) : (
          <button
            type="button"
            disabled
            className={cn('flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-muted text-muted-foreground text-[11px] font-semibold opacity-60', h)}
            aria-label="WhatsApp unavailable"
          >
            <svg viewBox="0 0 24 24" className={icon} fill="currentColor" aria-hidden="true">
              <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.42z" />
            </svg>
            <span className="hidden sm:inline">WhatsApp</span>
          </button>
        )}
        <button
          type="button"
          onClick={(e) => { stop(e); setCallOpen(true); }}
          className={cn('flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-[11px] font-semibold', h)}
          aria-label="Call"
        >
          <Phone className={icon} />
          <span className="hidden sm:inline">Call</span>
        </button>
      </div>

      <CallContactDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        agentName={agentName || 'Property Agent'}
        agencyName={agencyName || undefined}
        mobileNumber={phone || undefined}
        phoneNumber={phone || undefined}
        referenceId={referenceId || (propertyId || projectId || '').slice(0, 8) || 'N/A'}
        listingId={propertyId || projectId}
        listingType={listingType}
      />
      <EmailContactDialog
        open={msgOpen}
        onOpenChange={setMsgOpen}
        agentName={agentName || 'Property Agent'}
        agencyName={agencyName || undefined}
        agentLogo={agentLogo || undefined}
        phoneNumber={phone || undefined}
        referenceId={referenceId || (propertyId || projectId || '').slice(0, 8) || 'N/A'}
        propertyId={listingType === 'property' || listingType === 'commercial' || listingType === 'plot' ? propertyId : undefined}
        projectId={listingType === 'project' ? projectId : undefined}
      />
    </>
  );
}
